/** ATS-friendly CV form — data helpers. */

import { normalizeProjectUrl } from './atsCvProjectLink.js';
export const DEFAULT_ATS_SECTION_TITLES = {
    summary: "SUMMARY",
    experience: "PROFESSIONAL EXPERIENCE",
    education: "EDUCATION",
    projects: "PROJECTS",
    skills: "SKILLS",
    certifications: "CERTIFICATES",
    languages: "LANGUAGES",
};

export function createExperienceEntry(id) {
    return {
        id: id || String(Date.now()),
        jobTitle: "",
        company: "",
        location: "",
        startDate: "",
        endDate: "",
        isCurrent: false,
        description: "",
    };
}

export function createEducationEntry(id) {
    return {
        id: id || String(Date.now() + 1),
        degree: "",
        institution: "",
        location: "",
        graduationYear: "",
        details: "",
    };
}

export function createProjectEntry(id) {
    return {
        id: id || String(Date.now() + 2),
        name: "",
        link: "",
        technologies: "",
        date: "",
        description: "",
    };
}

/** @typedef {"bullets" | "twoColumn" | "paragraph"} CustomSectionLayout */

export function createCustomSection(id) {
    return {
        id: id || `custom-${Date.now()}`,
        title: "ADDITIONAL SECTION",
        layout: /** @type {CustomSectionLayout} */ ("bullets"),
        content: "",
    };
}

export const INITIAL_ATS_CV_FORM = {
    // A data URL, or "" for the usual photo-less CV. Kept on the form rather
    // than beside it so a restored draft brings the picture back with the text.
    photo: "",
    // The picture as it was picked, and the framing that turns it into `photo`.
    // Kept so "adjust" re-crops the ORIGINAL: re-cropping an already-cropped
    // square would cut a little more off on every visit to the dialog.
    photoSource: "",
    photoCrop: null,
    fullName: "",
    jobTitle: "",
    email: "",
    phone: "",
    location: "",
    linkedin: "",
    website: "",
    summary: "",
    skills: "",
    experiences: [createExperienceEntry("exp-1")],
    education: [createEducationEntry("edu-1")],
    projects: [],
    certifications: "",
    languages: "",
    sectionTitles: { ...DEFAULT_ATS_SECTION_TITLES },
    customSections: [],
};

export function displayField(value) {
    const v = String(value ?? "").trim();
    return v || "";
}

export function formatDateRange(exp) {
    const start = displayField(exp.startDate);
    const end = exp.isCurrent ? "Present" : displayField(exp.endDate);
    if (start && end) return `${start} – ${end}`;
    return start || end || "";
}

/** Split description into bullet lines. */
function stripListMarker(s) {
    return String(s ?? "")
        .replace(/^[\s•·▪◦‣⁃\-*–—]+/, "")
        .replace(/^\d+[.)]\s*/, "")
        .trim();
}

export function parseBulletLines(text) {
    return String(text ?? "")
        .split(/\n/)
        .map(stripListMarker)
        .filter(Boolean);
}

/** Skills, certs, languages — one item per line or comma. */
export function parseListItems(text) {
    return String(text ?? "")
        .split(/\n|,/)
        .map(stripListMarker)
        .filter(Boolean);
}

export function getSectionTitle(form, key) {
    const custom = form.sectionTitles?.[key];
    return displayField(custom) || DEFAULT_ATS_SECTION_TITLES[key] || key;
}

export function buildAtsCvData(form) {
    const titles = {
        ...DEFAULT_ATS_SECTION_TITLES,
        ...(form.sectionTitles || {}),
    };
    Object.keys(titles).forEach((k) => {
        titles[k] = displayField(titles[k]) || DEFAULT_ATS_SECTION_TITLES[k];
    });

    return {
        photo: String(form.photo || ""),
        // The shape of the photo box, so the preview and the PDF draw the
        // rectangle the crop dialog showed rather than assuming a square.
        photoAspect: Number(form.photoCrop?.aspect) > 0 ? Number(form.photoCrop.aspect) : 1,
        fullName: displayField(form.fullName),
        jobTitle: displayField(form.jobTitle),
        email: displayField(form.email),
        phone: displayField(form.phone),
        location: displayField(form.location),
        linkedin: displayField(form.linkedin),
        website: displayField(form.website),
        summary: displayField(form.summary),
        skills: parseListItems(form.skills),
        certifications: parseListItems(form.certifications),
        languages: parseListItems(form.languages),
        sectionTitles: titles,
        experiences: (form.experiences || [])
            .map((exp) => ({
                jobTitle: displayField(exp.jobTitle),
                company: displayField(exp.company),
                location: displayField(exp.location),
                dateRange: formatDateRange(exp),
                bullets: parseBulletLines(exp.description),
            }))
            .filter((exp) => exp.jobTitle || exp.company),
        education: (form.education || [])
            .map((edu) => ({
                degree: displayField(edu.degree),
                institution: displayField(edu.institution),
                location: displayField(edu.location),
                graduationYear: displayField(edu.graduationYear),
                details: displayField(edu.details),
                bullets: parseBulletLines(edu.details),
            }))
            .filter((edu) => edu.degree || edu.institution),
        projects: (form.projects || [])
            .map((proj) => ({
                name: displayField(proj.name),
                link: displayField(proj.link),
                linkHref: normalizeProjectUrl(proj.link),
                technologies: displayField(proj.technologies),
                date: displayField(proj.date),
                bullets: parseBulletLines(proj.description),
            }))
            .filter((proj) => proj.name),
        customSections: (form.customSections || [])
            .map((sec) => ({
                id: sec.id,
                title: displayField(sec.title),
                layout: sec.layout || "bullets",
                items: parseBulletLines(sec.content),
                paragraph:
                    sec.layout === "paragraph"
                        ? displayField(sec.content)
                        : "",
            }))
            .filter((sec) => sec.title && (sec.items.length > 0 || sec.paragraph)),
    };
}

function ocrTabPair(left, right) {
    const l = displayField(left);
    const r = displayField(right);
    if (l && r) return `${l}\t${r}`;
    return l || r || "";
}

function ocrBullet(text) {
    const t = displayField(text);
    return t ? `- ${t}` : "";
}

/**
 * Linear CV text in import-friendly order (tabs, ASCII bullets, section headers).
 * Used for OCR-ready PDF export and re-import.
 * @param {typeof INITIAL_ATS_CV_FORM} form
 * @returns {string[]}
 */
export function buildOcrReadyCvLines(form) {
    const d = buildAtsCvData(form);
    const t = d.sectionTitles;
    const lines = [];

    if (d.fullName) lines.push(d.fullName);
    if (d.jobTitle) lines.push(d.jobTitle);

    const contacts = [d.location, d.email, d.phone, d.linkedin, d.website].filter(Boolean);
    if (contacts.length) lines.push(contacts.join(" | "));

    const pushSection = (title, bodyLines) => {
        const body = bodyLines.filter(Boolean);
        if (!body.length) return;
        if (lines.length) lines.push("");
        lines.push(displayField(title).toUpperCase() || title);
        lines.push(...body);
    };

    if (d.summary) {
        pushSection(t.summary, [d.summary]);
    }

    if (d.experiences.length) {
        const body = [];
        d.experiences.forEach((exp) => {
            const row1 = ocrTabPair(exp.jobTitle, exp.dateRange);
            if (row1) body.push(row1);
            const row2 = ocrTabPair(exp.company, exp.location);
            if (row2) body.push(row2);
            exp.bullets.forEach((b) => body.push(ocrBullet(b)));
        });
        pushSection(t.experience, body);
    }

    if (d.education.length) {
        const body = [];
        d.education.forEach((edu) => {
            const row1 = ocrTabPair(edu.degree, edu.graduationYear);
            if (row1) body.push(row1);
            const row2 = ocrTabPair(edu.institution, edu.location);
            if (row2) body.push(row2);
            edu.bullets.forEach((b) => body.push(ocrBullet(b)));
        });
        pushSection(t.education, body);
    }

    if (d.projects.length) {
        const body = [];
        d.projects.forEach((proj) => {
            const row1 = ocrTabPair(proj.name, proj.date);
            if (row1) body.push(row1);
            if (proj.technologies) body.push(proj.technologies);
            if (proj.link) body.push(proj.link);
            proj.bullets.forEach((b) => body.push(ocrBullet(b)));
        });
        pushSection(t.projects, body);
    }

    if (d.skills.length) {
        pushSection(t.skills, d.skills.map((s) => ocrBullet(s)));
    }

    if (d.certifications.length) {
        pushSection(t.certifications, d.certifications.map((c) => ocrBullet(c)));
    }

    if (d.languages.length) {
        pushSection(t.languages, d.languages.map((l) => ocrBullet(l)));
    }

    d.customSections.forEach((sec) => {
        if (sec.layout === "paragraph" && sec.paragraph) {
            pushSection(sec.title, [sec.paragraph]);
        } else {
            pushSection(sec.title, sec.items.map((item) => ocrBullet(item)));
        }
    });

    return lines;
}

const FIELD_HISTORY_KEY = "ats-cv-form-field-history";
const FORM_SNAPSHOT_KEY = "ats-cv-form-last-snapshot";
const FIELD_HISTORY_MAX = 8;

/** @returns {string[]} */
export function getAtsCvFieldHistory(fieldName) {
    if (typeof window === "undefined" || !fieldName) return [];
    try {
        const store = JSON.parse(localStorage.getItem(FIELD_HISTORY_KEY) || "{}");
        const list = store[fieldName];
        return Array.isArray(list) ? list.filter((v) => typeof v === "string" && v.trim()) : [];
    } catch {
        return [];
    }
}

/** @returns {typeof INITIAL_ATS_CV_FORM | null} */
export function getAtsCvFormSnapshot() {
    if (typeof window === "undefined") return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        return { ...INITIAL_ATS_CV_FORM, ...JSON.parse(raw) };
    } catch {
        return null;
    }
}

/** Save latest CV for quick restore / suggestions. */
export function saveAtsCvFormSnapshot(form) {
    if (typeof window === "undefined" || !form) return;
    try {
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(form));
    } catch {
        /* ignore */
    }
}

/** Suggestions: local history + last snapshot + other site forms. */
export function getAtsCvFieldSuggestions(fieldName) {
    const history = getAtsCvFieldHistory(fieldName);
    const snapshot = getAtsCvFormSnapshot();
    const fromSnapshot = snapshot ? displayField(snapshot[fieldName]) : "";

    return [...new Set([...history, fromSnapshot].filter(Boolean))];
}

export function saveAtsCvFieldHistory(fieldName, value) {
    if (typeof window === "undefined" || !fieldName) return;
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return;

    try {
        const store = JSON.parse(localStorage.getItem(FIELD_HISTORY_KEY) || "{}");
        const prev = Array.isArray(store[fieldName]) ? store[fieldName] : [];
        const next = [trimmed, ...prev.filter((v) => v !== trimmed)].slice(0, FIELD_HISTORY_MAX);
        store[fieldName] = next;
        localStorage.setItem(FIELD_HISTORY_KEY, JSON.stringify(store));
    } catch {
        /* ignore */
    }
}

/** Persist common fields after export. */
export function saveAtsCvPersonalHistory(form) {
    if (!form) return;
    const fields = [
        "fullName",
        "jobTitle",
        "email",
        "phone",
        "location",
        "linkedin",
        "website",
    ];
    fields.forEach((key) => saveAtsCvFieldHistory(key, form[key]));
}
